import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];

interface IntentEntry {
  intent: string | null;
  count: number;
}

function Skeleton() {
  return (
    <div className="animate-pulse flex flex-col items-center justify-center h-[220px]">
      <div className="w-32 h-32 rounded-full bg-slate-800 mb-4" />
      <div className="h-3 w-48 bg-slate-800 rounded" />
    </div>
  );
}

export function IntentChart() {
  const [data, setData] = useState<IntentEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.calls.analytics.query({ tenantId: user.tenantId })
      .then(res => setData(res.intentDistribution ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const chartData = data.map((d, i) => ({
    name: d.intent ?? 'Unknown',
    value: d.count,
    color: COLORS[i % COLORS.length],
  }));

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-slate-200 mb-1">Intent Breakdown</h3>
      <p className="text-xs text-slate-500 mb-4">Distribution of call intents</p>
      {loading ? (
        <Skeleton />
      ) : chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[220px] text-slate-500 text-sm">
          No intent data available yet.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={3}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0', fontSize: '12px' }}
              formatter={(value: number) => [value, '']}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span style={{ color: '#94a3b8', fontSize: '12px' }}>{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
